import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import {
  GraduationCap, Users, FileText, Upload, Trophy, BarChart3, Download,
  Trash2, ChevronLeft, Loader2, CheckCircle2, XCircle, Clock, Sparkles,
  ArrowUpDown, Plus, UserPlus, FileSpreadsheet, AlertCircle, RefreshCw,
  Medal, Award, Star, TrendingUp, Filter, Search, MoreVertical, Eye,
  BookOpen, Building, Calendar, FileUp, X, Zap
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Tier badge colors
const tierColors = {
  starter: 'bg-green-100 text-green-800 border-green-200',
  standard: 'bg-blue-100 text-blue-800 border-blue-200',
  enterprise: 'bg-purple-100 text-purple-800 border-purple-200'
};

const tierLabels = {
  starter: 'Free (Beta)',
  standard: 'Premium',
  enterprise: 'Enterprise'
};

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800',
  processing: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800'
};

export default function CohortDetail() {
  const { cohortId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { toast } = useToast();

  // State
  const [cohort, setCohort] = useState(null);
  const [students, setStudents] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('students');

  // Modal states
  const [showAddStudentModal, setShowAddStudentModal] = useState(false);
  const [showUploadCSVModal, setShowUploadCSVModal] = useState(false);
  const [showBatchEvalModal, setShowBatchEvalModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentDetails, setStudentDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [generatingDetailedReport, setGeneratingDetailedReport] = useState({}); // Track which students are generating reports

  // Form states
  const [newStudent, setNewStudent] = useState({ roll_number: '', name: '', email: '' });
  const [csvFile, setCsvFile] = useState(null);
  const [paperFiles, setPaperFiles] = useState([]);
  const [batchJobName, setBatchJobName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [evaluating, setEvaluating] = useState(false);

  // Filters
  const [sortBy, setSortBy] = useState('oi_score');
  const [sortOrder, setSortOrder] = useState('desc');
  const [searchQuery, setSearchQuery] = useState('');

  // Refresh handler with feedback
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const [cohortRes, studentsRes, jobsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/educator/cohorts/${cohortId}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/educator/cohorts/${cohortId}/students`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/educator/cohorts/${cohortId}/jobs`, { withCredentials: true })
      ]);

      if (cohortRes.data.success) setCohort(cohortRes.data.cohort);
      if (studentsRes.data.success) setStudents(studentsRes.data.students);
      if (jobsRes.data.success) setJobs(jobsRes.data.jobs);
      
      toast({
        title: "Refreshed",
        description: `${studentsRes.data.students?.length || 0} students, ${jobsRes.data.jobs?.length || 0} jobs loaded`
      });
    } catch (error) {
      console.error('Error refreshing:', error);
      toast({
        title: "Refresh Failed",
        description: error.response?.data?.detail || "Could not refresh data",
        variant: "destructive"
      });
    } finally {
      setRefreshing(false);
    }
  };

  // Load cohort data
  const loadCohortData = useCallback(async () => {
    try {
      const [cohortRes, studentsRes, jobsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/educator/cohorts/${cohortId}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/educator/cohorts/${cohortId}/students`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/educator/cohorts/${cohortId}/jobs`, { withCredentials: true })
      ]);

      if (cohortRes.data.success) setCohort(cohortRes.data.cohort);
      if (studentsRes.data.success) setStudents(studentsRes.data.students);
      if (jobsRes.data.success) setJobs(jobsRes.data.jobs);
    } catch (error) {
      console.error('Error loading cohort:', error);
      if (error.response?.status === 401) {
        toast({
          title: "Authentication Required",
          description: "Please log in to access this cohort",
          variant: "destructive"
        });
        navigate('/login');
      } else if (error.response?.status === 404) {
        toast({
          title: "Cohort Not Found",
          description: "This cohort doesn't exist or you don't have access",
          variant: "destructive"
        });
        navigate('/educator');
      }
    } finally {
      setLoading(false);
    }
  }, [cohortId, navigate, toast]);

  // Load leaderboard
  const loadLeaderboard = useCallback(async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/educator/cohorts/${cohortId}/leaderboard`,
        {
          params: { sort_by: sortBy, order: sortOrder },
          withCredentials: true
        }
      );
      if (response.data.success) {
        setLeaderboard(response.data.leaderboard);
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error('Error loading leaderboard:', error);
    }
  }, [cohortId, sortBy, sortOrder]);

  useEffect(() => {
    if (isAuthenticated) {
      loadCohortData();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, loadCohortData]);

  useEffect(() => {
    if (isAuthenticated && activeTab === 'leaderboard') {
      loadLeaderboard();
    }
  }, [isAuthenticated, activeTab, loadLeaderboard]);

  // Add single student
  const handleAddStudent = async () => {
    if (!newStudent.roll_number.trim() || !newStudent.name.trim()) {
      toast({
        title: "Missing Information",
        description: "Roll number and name are required",
        variant: "destructive"
      });
      return;
    }

    setUploading(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/educator/cohorts/${cohortId}/students`,
        newStudent,
        { withCredentials: true }
      );

      if (response.data.success) {
        toast({ title: "Student Added", description: `${newStudent.name} has been added to the roster` });
        setStudents([...students, response.data.student]);
        setShowAddStudentModal(false);
        setNewStudent({ roll_number: '', name: '', email: '' });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to add student",
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  };

  // Upload CSV roster
  const handleUploadCSV = async () => {
    if (!csvFile) {
      toast({
        title: "No File Selected",
        description: "Please select a CSV file to upload",
        variant: "destructive"
      });
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', csvFile);

      const response = await axios.post(
        `${BACKEND_URL}/api/educator/cohorts/${cohortId}/students/csv`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true
        }
      );

      if (response.data.success) {
        toast({
          title: "Roster Uploaded",
          description: `Added ${response.data.success_count} students${response.data.error_count > 0 ? `, ${response.data.error_count} duplicates skipped` : ''}`
        });
        setShowUploadCSVModal(false);
        setCsvFile(null);
        loadCohortData(); // Refresh to get updated student list
      }
    } catch (error) {
      toast({
        title: "Upload Failed",
        description: error.response?.data?.detail || "Failed to upload roster",
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  };

  // Delete student
  const handleDeleteStudent = async (studentId, studentName) => {
    if (!window.confirm(`Remove ${studentName} from this cohort?`)) return;

    try {
      const response = await axios.delete(
        `${BACKEND_URL}/api/educator/cohorts/${cohortId}/students/${studentId}`,
        { withCredentials: true }
      );

      if (response.data.success) {
        toast({ title: "Student Removed", description: `${studentName} has been removed` });
        setStudents(students.filter(s => s.id !== studentId));
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to remove student",
        variant: "destructive"
      });
    }
  };

  // Start batch evaluation
  const handleStartBatchEval = async () => {
    if (paperFiles.length === 0) {
      toast({
        title: "No Papers Selected",
        description: "Please upload at least one paper to evaluate",
        variant: "destructive"
      });
      return;
    }

    if (!batchJobName.trim()) {
      toast({
        title: "Name Required",
        description: "Please enter a name for this batch evaluation",
        variant: "destructive"
      });
      return;
    }

    if (paperFiles.length > (cohort?.max_papers || 30)) {
      toast({
        title: "Tier Limit Exceeded",
        description: `Your ${cohort?.tier} tier allows up to ${cohort?.max_papers} papers. Please upgrade or reduce the number of files.`,
        variant: "destructive"
      });
      return;
    }

    setEvaluating(true);
    try {
      const formData = new FormData();
      formData.append('job_name', batchJobName);
      paperFiles.forEach(file => {
        formData.append('files', file);
      });

      const response = await axios.post(
        `${BACKEND_URL}/api/educator/cohorts/${cohortId}/batch-evaluate`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true
        }
      );

      if (response.data.success) {
        toast({
          title: "Batch Evaluation Started!",
          description: `Processing ${paperFiles.length} papers. Check progress in the Jobs tab.`
        });
        setShowBatchEvalModal(false);
        setPaperFiles([]);
        setBatchJobName('');
        setActiveTab('jobs');
        loadCohortData(); // Refresh jobs
      }
    } catch (error) {
      toast({
        title: "Evaluation Failed",
        description: error.response?.data?.detail || "Failed to start batch evaluation",
        variant: "destructive"
      });
    } finally {
      setEvaluating(false);
    }
  };

  // Export CSV
  const handleExportCSV = async () => {
    try {
      // Get auth token from localStorage
      const token = localStorage.getItem('if_auth_token');
      
      const response = await axios.get(
        `${BACKEND_URL}/api/educator/cohorts/${cohortId}/export/csv`,
        {
          withCredentials: true,
          responseType: 'blob',
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${cohort?.name?.replace(/\s+/g, '_') || 'cohort'}_results.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast({ title: "Export Complete", description: "Results exported to CSV" });
    } catch (error) {
      console.error('Export error:', error);
      toast({
        title: "Export Failed",
        description: error.response?.data?.detail || "Failed to export results. Please try again.",
        variant: "destructive"
      });
    }
  };

  // View student evaluation details
  const handleViewDetails = async (student) => {
    setSelectedStudent(student);
    setShowDetailModal(true);
    setLoadingDetails(true);
    
    try {
      const token = localStorage.getItem('if_auth_token');
      const response = await axios.get(
        `${BACKEND_URL}/api/educator/cohorts/${cohortId}/students/${student.id}/details`,
        {
          withCredentials: true,
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        }
      );
      
      if (response.data.success) {
        setStudentDetails(response.data.details);
      }
    } catch (error) {
      console.error('Error fetching student details:', error);
      // If endpoint doesn't exist, just show basic info from the student object
      setStudentDetails({
        ...student,
        summary: student.evaluation_summary || null,
        feedback: student.feedback || null
      });
    } finally {
      setLoadingDetails(false);
    }
  };

  // Generate detailed report for a student
  const [generatingReport, setGeneratingReport] = useState(false);
  
  const handleGenerateDetailedReport = async () => {
    if (!selectedStudent) return;
    
    setGeneratingReport(true);
    try {
      const token = localStorage.getItem('if_auth_token');
      const response = await axios.post(
        `${BACKEND_URL}/api/educator/cohorts/${cohortId}/students/${selectedStudent.id}/generate-detailed-report`,
        {},
        {
          withCredentials: true,
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        }
      );
      
      if (response.data.success) {
        // Update the studentDetails with the new detailed data
        setStudentDetails(prev => ({
          ...prev,
          evaluation_details: response.data.details
        }));
        
        toast({
          title: response.data.already_generated ? "Report Already Exists" : "Report Generated!",
          description: response.data.synthetic 
            ? "Detailed report generated based on evaluation scores."
            : "Full detailed evaluation report is now available.",
        });
      } else {
        // Handle the case where paper content is not available
        toast({
          title: "Cannot Generate Report",
          description: response.data.error || response.data.message || "Paper content not available for detailed analysis.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error generating detailed report:', error);
      toast({
        title: "Generation Failed",
        description: error.response?.data?.error || error.response?.data?.detail || "Failed to generate detailed report. Please try again.",
        variant: "destructive"
      });
    } finally {
      setGeneratingReport(false);
    }
  };

  // Generate detailed report for a student from the student list (not modal)
  const handleGenerateDetailedReportForStudent = async (student) => {
    setGeneratingDetailedReport(prev => ({ ...prev, [student.id]: true }));
    
    try {
      const token = localStorage.getItem('if_auth_token');
      const response = await axios.post(
        `${BACKEND_URL}/api/educator/cohorts/${cohortId}/students/${student.id}/generate-detailed-report`,
        {},
        {
          withCredentials: true,
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        }
      );
      
      if (response.data.success) {
        // Update the student in the list to show detailed report is available
        setStudents(prevStudents => 
          prevStudents.map(s => 
            s.id === student.id 
              ? { ...s, has_detailed_report: true, evaluation_details: response.data.details }
              : s
          )
        );
        
        toast({
          title: "Detailed Report Ready!",
          description: `Full analysis generated for ${student.name}`,
        });
      } else {
        toast({
          title: "Cannot Generate Report",
          description: response.data.error || "Paper content not available.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error generating detailed report:', error);
      toast({
        title: "Generation Failed",
        description: error.response?.data?.detail || "Failed to generate detailed report.",
        variant: "destructive"
      });
    } finally {
      setGeneratingDetailedReport(prev => ({ ...prev, [student.id]: false }));
    }
  };

  // Filter students by search
  const filteredStudents = students.filter(s =>
    s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.roll_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (s.email && s.email.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
      </div>
    );
  }

  // Not authenticated
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 py-12">
        <div className="container mx-auto px-4 max-w-2xl">
          <Card className="border-2 border-purple-200">
            <CardContent className="pt-12 pb-8 text-center">
              <GraduationCap className="w-16 h-16 mx-auto text-purple-600 mb-4" />
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Authentication Required</h2>
              <p className="text-slate-600 mb-6">Please sign in to access this cohort</p>
              <Button onClick={() => navigate('/login')} className="bg-purple-600 hover:bg-purple-700">
                Sign In
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Cohort not found
  if (!cohort) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 py-12">
        <div className="container mx-auto px-4 max-w-2xl">
          <Card className="border-2 border-red-200">
            <CardContent className="pt-12 pb-8 text-center">
              <AlertCircle className="w-16 h-16 mx-auto text-red-500 mb-4" />
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Cohort Not Found</h2>
              <p className="text-slate-600 mb-6">This cohort does not exist or you do not have access</p>
              <Button onClick={() => navigate('/educator')} variant="outline">
                <ChevronLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 py-8">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate('/educator')}>
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-slate-900">{cohort.name}</h1>
              <Badge className={tierColors[cohort.tier]}>{tierLabels[cohort.tier]}</Badge>
            </div>
            <div className="flex items-center gap-4 text-sm text-slate-600 mt-1">
              {cohort.subject && (
                <span className="flex items-center gap-1">
                  <BookOpen className="w-4 h-4" />
                  {cohort.subject}
                </span>
              )}
              {cohort.semester && (
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {cohort.semester}
                </span>
              )}
              {cohort.institution && (
                <span className="flex items-center gap-1">
                  <Building className="w-4 h-4" />
                  {cohort.institution}
                </span>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleExportCSV}>
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
            <Button className="bg-purple-600 hover:bg-purple-700" onClick={() => setShowBatchEvalModal(true)}>
              <Upload className="w-4 h-4 mr-2" />
              Batch Evaluate
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{students.length}</p>
                  <p className="text-sm text-slate-600">Students</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{cohort.evaluation_count || 0}</p>
                  <p className="text-sm text-slate-600">Evaluated</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <Trophy className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {stats?.avg_oi_score?.toFixed(1) || '-'}
                  </p>
                  <p className="text-sm text-slate-600">Avg OI Score</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{cohort.max_papers}</p>
                  <p className="text-sm text-slate-600">Max Papers</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="students" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Students ({students.length})
            </TabsTrigger>
            <TabsTrigger value="jobs" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Batch Jobs ({jobs.length})
            </TabsTrigger>
            <TabsTrigger value="leaderboard" className="flex items-center gap-2">
              <Trophy className="w-4 h-4" />
              Leaderboard
            </TabsTrigger>
          </TabsList>

          {/* Students Tab */}
          <TabsContent value="students">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Student Roster</CardTitle>
                  <CardDescription>Manage students in this cohort</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing}>
                    {refreshing ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4 mr-2" />
                    )}
                    Refresh
                  </Button>
                  <Button variant="outline" onClick={() => setShowUploadCSVModal(true)}>
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    Upload CSV
                  </Button>
                  <Button onClick={() => setShowAddStudentModal(true)}>
                    <UserPlus className="w-4 h-4 mr-2" />
                    Add Student
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Pending students alert */}
                {students.filter(s => s.evaluation_status === 'pending' || !s.evaluation_status).length > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <AlertCircle className="w-5 h-5 text-amber-600" />
                        <div>
                          <p className="font-medium text-amber-800">
                            {students.filter(s => s.evaluation_status === 'pending' || !s.evaluation_status).length} students pending evaluation
                          </p>
                          <p className="text-sm text-amber-700">Re-upload papers using batch evaluation to process</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="text-red-600 border-red-300 hover:bg-red-50"
                          onClick={async () => {
                            if (window.confirm('Delete all pending students without evaluations? This cannot be undone.')) {
                              const pendingIds = students
                                .filter(s => s.evaluation_status === 'pending' || !s.evaluation_status)
                                .map(s => s.id);
                              for (const id of pendingIds) {
                                try {
                                  await axios.delete(
                                    `${BACKEND_URL}/api/educator/cohorts/${cohortId}/students/${id}`,
                                    { withCredentials: true }
                                  );
                                } catch (e) { /* ignore */ }
                              }
                              toast({ title: "Cleanup Complete", description: `Removed ${pendingIds.length} pending students` });
                              loadCohortData();
                            }
                          }}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Clear Pending
                        </Button>
                        <Button 
                          size="sm" 
                          className="bg-amber-600 hover:bg-amber-700"
                          onClick={() => navigate(`/educator/cohort/${cohortId}/batch`)}
                        >
                          <Sparkles className="w-4 h-4 mr-2" />
                          Re-upload & Evaluate
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Search */}
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search by name, roll number, or email..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>

                {/* Student List */}
                {filteredStudents.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                    <p className="text-slate-600 mb-4">
                      {students.length === 0 ? 'No students yet' : 'No students match your search'}
                    </p>
                    {students.length === 0 && (
                      <div className="flex justify-center gap-3">
                        <Button variant="outline" onClick={() => setShowUploadCSVModal(true)}>
                          <FileSpreadsheet className="w-4 h-4 mr-2" />
                          Upload CSV Roster
                        </Button>
                        <Button onClick={() => setShowAddStudentModal(true)}>
                          <UserPlus className="w-4 h-4 mr-2" />
                          Add Manually
                        </Button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-slate-50 border-b">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Roll #</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Name</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Email</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Status</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">OI Score</th>
                          <th className="px-4 py-3 text-right text-sm font-medium text-slate-600">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredStudents.map(student => (
                          <tr key={student.id} className="border-b last:border-b-0 hover:bg-slate-50">
                            <td className="px-4 py-3 font-mono text-sm">{student.roll_number}</td>
                            <td className="px-4 py-3 font-medium">{student.name}</td>
                            <td className="px-4 py-3 text-sm text-slate-600">{student.email || '-'}</td>
                            <td className="px-4 py-3">
                              <Badge className={statusColors[student.evaluation_status || 'pending']}>
                                {student.evaluation_status || 'pending'}
                              </Badge>
                            </td>
                            <td className="px-4 py-3">
                              {student.oi_score ? (
                                <button 
                                  onClick={() => handleViewDetails(student)}
                                  className="font-semibold text-purple-700 hover:text-purple-900 hover:underline cursor-pointer"
                                >
                                  {student.oi_score.toFixed(1)}/10
                                </button>
                              ) : '-'}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <div className="flex items-center justify-end gap-1">
                                {student.evaluation_status === 'completed' && (
                                  <>
                                    {/* View Brief Report */}
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                                      onClick={() => handleViewDetails(student)}
                                      title="View Report"
                                      data-testid={`view-report-btn-${student.id}`}
                                    >
                                      <Eye className="w-4 h-4" />
                                    </Button>
                                    
                                    {/* View Detailed Report / Generate Button */}
                                    {student.has_detailed_report ? (
                                      // Already has detailed report - show view button
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                        onClick={() => handleViewDetails(student)}
                                        title="View Full Analysis"
                                        data-testid={`view-full-analysis-btn-${student.id}`}
                                      >
                                        <BookOpen className="w-4 h-4" />
                                      </Button>
                                    ) : (
                                      // No detailed report yet - show generate button
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                        onClick={() => handleGenerateDetailedReportForStudent(student)}
                                        disabled={generatingDetailedReport[student.id]}
                                        title="View Detailed Report (generates full analysis)"
                                        data-testid={`generate-detailed-btn-${student.id}`}
                                      >
                                        {generatingDetailedReport[student.id] ? (
                                          <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                          <FileText className="w-4 h-4" />
                                        )}
                                      </Button>
                                    )}
                                    
                                    {/* PDF Download */}
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                      onClick={async () => {
                                        try {
                                          const token = localStorage.getItem('if_auth_token');
                                          const response = await fetch(
                                            `${BACKEND_URL}/api/educator/cohorts/${cohortId}/students/${student.id}/pdf`,
                                            { headers: { 'Authorization': `Bearer ${token}` } }
                                          );
                                          if (!response.ok) throw new Error('Failed to download');
                                          const blob = await response.blob();
                                          const url = window.URL.createObjectURL(blob);
                                          const a = document.createElement('a');
                                          a.href = url;
                                          a.download = `IF_Report_${student.roll_number}_${student.name.replace(/ /g, '_')}.pdf`;
                                          document.body.appendChild(a);
                                          a.click();
                                          window.URL.revokeObjectURL(url);
                                          document.body.removeChild(a);
                                          toast({ title: "PDF Downloaded", description: "Report also sent to your email" });
                                        } catch (error) {
                                          toast({ title: "Download Failed", description: "Could not download PDF", variant: "destructive" });
                                        }
                                      }}
                                      title="Download PDF"
                                      data-testid={`download-pdf-btn-${student.id}`}
                                    >
                                      <Download className="w-4 h-4" />
                                    </Button>
                                  </>
                                )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => handleDeleteStudent(student.id, student.name)}
                                  data-testid={`delete-student-btn-${student.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Jobs Tab */}
          <TabsContent value="jobs">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Batch Evaluation Jobs</CardTitle>
                  <CardDescription>Track progress of batch evaluations</CardDescription>
                </div>
                <Button onClick={handleRefresh} variant="outline" size="sm" disabled={refreshing}>
                  {refreshing ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  Refresh
                </Button>
              </CardHeader>
              <CardContent>
                {jobs.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                    <p className="text-slate-600 mb-4">No batch evaluations yet</p>
                    <Button onClick={() => setShowBatchEvalModal(true)}>
                      <Upload className="w-4 h-4 mr-2" />
                      Start First Batch
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {jobs.map(job => (
                      <Card key={job.id} className="border">
                        <CardContent className="py-4">
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <h4 className="font-semibold text-slate-900">{job.name}</h4>
                              <p className="text-sm text-slate-600">
                                {job.total_papers} papers • Started {new Date(job.created_at).toLocaleString()}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge className={statusColors[job.status]}>
                                {job.status}
                              </Badge>
                              {job.status === 'processing' && job.processed_papers === 0 && (
                                <Badge className="bg-orange-100 text-orange-800 border-orange-300">
                                  Stuck
                                </Badge>
                              )}
                            </div>
                          </div>
                          <div className="space-y-2">
                            <div className="flex justify-between text-sm">
                              <span className="text-slate-600">
                                {job.processed_papers}/{job.total_papers} processed
                                {job.failed_papers > 0 && (
                                  <span className="text-red-600 ml-2">({job.failed_papers} failed)</span>
                                )}
                              </span>
                              <span className="font-medium">{job.progress_pct?.toFixed(0) || 0}%</span>
                            </div>
                            <Progress value={job.progress_pct || 0} className="h-2" />
                          </div>
                          {job.status === 'completed' && job.completed_at && (
                            <p className="text-sm text-green-600 mt-2 flex items-center gap-1">
                              <CheckCircle2 className="w-4 h-4" />
                              Completed {new Date(job.completed_at).toLocaleString()}
                            </p>
                          )}
                          {/* Stuck job helper */}
                          {job.status === 'processing' && job.processed_papers === 0 && (
                            <div className="mt-3 pt-3 border-t flex items-center justify-between">
                              <p className="text-sm text-orange-700">
                                This job appears stuck. Try re-evaluating from the batch page.
                              </p>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => navigate(`/educator/cohort/${cohortId}/batch`)}
                              >
                                <RefreshCw className="w-4 h-4 mr-2" />
                                Re-evaluate
                              </Button>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Leaderboard Tab */}
          <TabsContent value="leaderboard">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Performance Leaderboard</CardTitle>
                  <CardDescription>Rankings based on OI scores</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="border rounded-md px-3 py-2 text-sm"
                  >
                    <option value="oi_score">OI Score</option>
                    <option value="novelty_score">Novelty</option>
                    <option value="resonance_score">Resonance</option>
                    <option value="synthesis_score">Synthesis</option>
                    <option value="ethical_score">Ethical</option>
                    <option value="msa_spark_pct">Spark %</option>
                  </select>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
                  >
                    <ArrowUpDown className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={loadLeaderboard}>
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {leaderboard.length === 0 ? (
                  <div className="text-center py-12">
                    <Trophy className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                    <p className="text-slate-600">No evaluated papers yet</p>
                    <p className="text-sm text-slate-500 mt-1">Start a batch evaluation to see rankings</p>
                  </div>
                ) : (
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-slate-50 border-b">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Rank</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Student</th>
                          <th className="px-4 py-3 text-center text-sm font-medium text-slate-600">OI Score</th>
                          <th className="px-4 py-3 text-center text-sm font-medium text-slate-600">Novelty</th>
                          <th className="px-4 py-3 text-center text-sm font-medium text-slate-600">Resonance</th>
                          <th className="px-4 py-3 text-center text-sm font-medium text-slate-600">Synthesis</th>
                          <th className="px-4 py-3 text-center text-sm font-medium text-slate-600">Ethical</th>
                          <th className="px-4 py-3 text-center text-sm font-medium text-slate-600">Spark %</th>
                          <th className="px-4 py-3 text-center text-sm font-medium text-slate-600">Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        {leaderboard.map((student, index) => (
                          <tr key={student.id} className="border-b last:border-b-0 hover:bg-slate-50">
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                {student.rank === 1 && <Medal className="w-5 h-5 text-amber-500" />}
                                {student.rank === 2 && <Medal className="w-5 h-5 text-slate-400" />}
                                {student.rank === 3 && <Medal className="w-5 h-5 text-amber-700" />}
                                {student.rank > 3 && <span className="w-5 text-center text-slate-600">#{student.rank}</span>}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div>
                                <p className="font-medium">{student.name}</p>
                                <p className="text-xs text-slate-500">{student.roll_number}</p>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <button 
                                onClick={() => handleViewDetails(student)}
                                className="font-bold text-lg text-purple-700 hover:text-purple-900 hover:underline cursor-pointer"
                              >
                                {student.oi_score?.toFixed(1)}
                              </button>
                            </td>
                            <td className="px-4 py-3 text-center text-sm">{student.novelty_score?.toFixed(1) || '-'}</td>
                            <td className="px-4 py-3 text-center text-sm">{student.resonance_score?.toFixed(1) || '-'}</td>
                            <td className="px-4 py-3 text-center text-sm">{student.synthesis_score?.toFixed(1) || '-'}</td>
                            <td className="px-4 py-3 text-center text-sm">{student.ethical_score?.toFixed(1) || '-'}</td>
                            <td className="px-4 py-3 text-center">
                              <Badge className={student.msa_spark_pct >= 50 ? 'bg-orange-100 text-orange-800' : 'bg-blue-100 text-blue-800'}>
                                {student.msa_spark_pct?.toFixed(0) || '-'}%
                              </Badge>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                                onClick={() => handleViewDetails(student)}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Stats Summary */}
                {stats && stats.total_evaluated > 0 && (
                  <div className="mt-6 grid grid-cols-4 gap-4">
                    <div className="bg-slate-50 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-slate-900">{stats.total_evaluated}</p>
                      <p className="text-sm text-slate-600">Evaluated</p>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-purple-700">{stats.avg_oi_score?.toFixed(2)}</p>
                      <p className="text-sm text-purple-600">Avg OI Score</p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-green-700">{stats.max_oi_score?.toFixed(2)}</p>
                      <p className="text-sm text-green-600">Highest OI</p>
                    </div>
                    <div className="bg-orange-50 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-orange-700">{stats.avg_spark_pct?.toFixed(0)}%</p>
                      <p className="text-sm text-orange-600">Avg Spark</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Add Student Modal */}
        {showAddStudentModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-md">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <UserPlus className="w-5 h-5 text-purple-600" />
                  Add Student
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Roll Number *</label>
                  <Input
                    placeholder="e.g., 2024CS001"
                    value={newStudent.roll_number}
                    onChange={(e) => setNewStudent({ ...newStudent, roll_number: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Name *</label>
                  <Input
                    placeholder="Full name"
                    value={newStudent.name}
                    onChange={(e) => setNewStudent({ ...newStudent, name: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Email (optional)</label>
                  <Input
                    type="email"
                    placeholder="student@example.com"
                    value={newStudent.email}
                    onChange={(e) => setNewStudent({ ...newStudent, email: e.target.value })}
                  />
                </div>
                <div className="flex gap-3 pt-2">
                  <Button variant="outline" className="flex-1" onClick={() => setShowAddStudentModal(false)}>
                    Cancel
                  </Button>
                  <Button className="flex-1 bg-purple-600 hover:bg-purple-700" onClick={handleAddStudent} disabled={uploading}>
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Student'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Upload CSV Modal */}
        {showUploadCSVModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-md">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileSpreadsheet className="w-5 h-5 text-purple-600" />
                  Upload Student Roster
                </CardTitle>
                <CardDescription>
                  CSV should have columns: roll_number, name, email (optional)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div
                  className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-purple-400 transition-colors cursor-pointer"
                  onClick={() => document.getElementById('csv-upload').click()}
                >
                  <input
                    id="csv-upload"
                    type="file"
                    accept=".csv"
                    className="hidden"
                    onChange={(e) => setCsvFile(e.target.files[0])}
                  />
                  {csvFile ? (
                    <div className="flex items-center justify-center gap-2">
                      <FileSpreadsheet className="w-8 h-8 text-green-600" />
                      <span className="font-medium text-slate-900">{csvFile.name}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); setCsvFile(null); }}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-12 h-12 mx-auto text-slate-400 mb-2" />
                      <p className="text-slate-600">Click to select CSV file</p>
                      <p className="text-sm text-slate-500 mt-1">or drag and drop</p>
                    </>
                  )}
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-slate-600 font-medium mb-1">CSV Format Example:</p>
                  <code className="text-xs text-slate-700">roll_number,name,email</code><br />
                  <code className="text-xs text-slate-700">2024CS001,John Doe,john@example.com</code>
                </div>
                <div className="flex gap-3 pt-2">
                  <Button variant="outline" className="flex-1" onClick={() => { setShowUploadCSVModal(false); setCsvFile(null); }}>
                    Cancel
                  </Button>
                  <Button
                    className="flex-1 bg-purple-600 hover:bg-purple-700"
                    onClick={handleUploadCSV}
                    disabled={!csvFile || uploading}
                  >
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Upload Roster'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Batch Evaluation Modal */}
        {showBatchEvalModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Upload className="w-5 h-5 text-purple-600" />
                  Batch Evaluate Papers
                </CardTitle>
                <CardDescription>
                  Choose how you want to evaluate papers
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Option 1: Enhanced Batch Evaluation */}
                <div 
                  className="border-2 border-purple-200 bg-purple-50 rounded-lg p-4 cursor-pointer hover:border-purple-400 transition-colors"
                  onClick={() => navigate(`/educator/cohort/${cohortId}/batch`)}
                >
                  <div className="flex items-start gap-3">
                    <div className="bg-purple-100 rounded-lg p-2">
                      <Sparkles className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-purple-900">Full Evaluation Experience</h3>
                      <p className="text-sm text-purple-700 mt-1">
                        Upload → Preview with auto-matching → Watch live evaluations with detailed feedback → Class analytics & leaderboard
                      </p>
                      <Badge className="mt-2 bg-purple-100 text-purple-800">Recommended</Badge>
                    </div>
                  </div>
                </div>

                {/* Option 2: Quick Upload (existing flow) */}
                <div className="border border-slate-200 rounded-lg p-4">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="bg-slate-100 rounded-lg p-2">
                      <FileUp className="w-6 h-6 text-slate-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">Quick Background Upload</h3>
                      <p className="text-sm text-slate-600 mt-1">
                        Upload files and process in background. Check results later.
                      </p>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <Input
                      placeholder="Batch name (e.g., Midterm Evaluation)"
                      value={batchJobName}
                      onChange={(e) => setBatchJobName(e.target.value)}
                    />
                    <div
                      className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center hover:border-purple-400 transition-colors cursor-pointer"
                      onClick={(e) => { e.stopPropagation(); document.getElementById('paper-upload').click(); }}
                      onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-purple-500', 'bg-purple-50'); }}
                      onDragLeave={(e) => { e.currentTarget.classList.remove('border-purple-500', 'bg-purple-50'); }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.currentTarget.classList.remove('border-purple-500', 'bg-purple-50');
                        const droppedFiles = Array.from(e.dataTransfer.files);
                        setPaperFiles(prev => [...prev, ...droppedFiles]);
                      }}
                    >
                      <input
                        id="paper-upload"
                        type="file"
                        accept=".pdf,.doc,.docx,.txt,.xlsx,.pptx,.csv,.zip"
                        multiple
                        className="hidden"
                        onChange={(e) => setPaperFiles(prev => [...prev, ...Array.from(e.target.files)])}
                      />
                      {paperFiles.length > 0 ? (
                        <div>
                          <CheckCircle2 className="w-8 h-8 mx-auto text-green-600 mb-1" />
                          <p className="font-medium text-sm">{paperFiles.length} file(s) selected</p>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="mt-1"
                            onClick={(e) => { e.stopPropagation(); setPaperFiles([]); }}
                          >
                            Clear
                          </Button>
                        </div>
                      ) : (
                        <>
                          <FileUp className="w-8 h-8 mx-auto text-slate-400 mb-1" />
                          <p className="text-sm text-slate-600">Click to select or drag files</p>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {paperFiles.length > cohort?.max_papers && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-red-700">
                    <AlertCircle className="w-5 h-5" />
                    <span className="text-sm">
                      Exceeds tier limit ({cohort?.max_papers} papers). Remove some files or upgrade.
                    </span>
                  </div>
                )}

                <div className="bg-purple-50 rounded-lg p-3 space-y-2">
                  <p className="text-sm text-purple-700">
                    <Sparkles className="w-4 h-4 inline mr-1" />
                    <strong>Auto-matching:</strong> Papers are matched by filename (e.g., 2024CS001.pdf → Roll #2024CS001)
                  </p>
                  <p className="text-xs text-purple-600">
                    <strong>Supported formats:</strong> PDF, DOCX, TXT, XLSX, PPTX, CSV, ZIP (containing any of these)
                  </p>
                  <p className="text-xs text-purple-600">
                    <strong>Tip:</strong> Upload a ZIP file with all papers for bulk processing
                  </p>
                </div>

                <div className="flex gap-3 pt-2">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => { setShowBatchEvalModal(false); setPaperFiles([]); setBatchJobName(''); }}
                  >
                    Cancel
                  </Button>
                  <Button
                    className="flex-1 bg-purple-600 hover:bg-purple-700"
                    onClick={handleStartBatchEval}
                    disabled={evaluating || paperFiles.length === 0 || paperFiles.length > cohort?.max_papers}
                  >
                    {evaluating ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Starting...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Start Evaluation
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Student Details Modal - Matching QuickEvaluate Report Style */}
        {showDetailModal && selectedStudent && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <Card className="w-full max-w-5xl my-8 overflow-hidden">
              {/* Top gradient bar */}
              <div className="h-2 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500"></div>
              
              <CardHeader className="border-b bg-gradient-to-r from-purple-50 via-pink-50 to-orange-50">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-2xl">
                      <Trophy className="w-7 h-7 text-purple-600" />
                      Evaluation Report
                    </CardTitle>
                    <CardDescription className="mt-1">
                      <span className="font-semibold text-slate-700 text-lg">{selectedStudent.name}</span>
                      <span className="text-slate-500"> • {selectedStudent.roll_number}</span>
                      {selectedStudent.email && <span className="text-slate-500"> • {selectedStudent.email}</span>}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    {selectedStudent.evaluation_status === 'completed' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={async () => {
                          try {
                            const token = localStorage.getItem('if_auth_token');
                            const response = await fetch(
                              `${BACKEND_URL}/api/educator/cohorts/${cohortId}/students/${selectedStudent.id}/pdf`,
                              {
                                headers: {
                                  'Authorization': `Bearer ${token}`
                                }
                              }
                            );
                            if (!response.ok) {
                              const err = await response.json();
                              throw new Error(err.detail || 'Failed to download PDF');
                            }
                            const blob = await response.blob();
                            const url = window.URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `IF_Report_${selectedStudent.roll_number}_${selectedStudent.name.replace(/ /g, '_')}.pdf`;
                            document.body.appendChild(a);
                            a.click();
                            window.URL.revokeObjectURL(url);
                            document.body.removeChild(a);
                            toast({
                              title: "PDF Downloaded",
                              description: "Report also sent to your email"
                            });
                          } catch (error) {
                            console.error('Error downloading PDF:', error);
                            toast({
                              title: "Download Failed",
                              description: error.message || "Could not download PDF report",
                              variant: "destructive"
                            });
                          }
                        }}
                        className="border-purple-300 text-purple-700 hover:bg-purple-50"
                        data-testid="download-pdf-btn"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download PDF
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setShowDetailModal(false); setSelectedStudent(null); setStudentDetails(null); }}
                      className="hover:bg-purple-100"
                    >
                      <X className="w-5 h-5" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="pt-6 bg-gradient-to-br from-purple-50/30 via-pink-50/30 to-orange-50/30">
                {loadingDetails ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
                  </div>
                ) : (
                  <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-2">
                    
                    {/* Paper Summary Card */}
                    {studentDetails?.evaluation_details?.paper_summary && (
                      <Card className="border-2 border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50">
                        <CardHeader className="pb-2">
                          <CardTitle className="flex items-center gap-2 text-lg text-blue-900">
                            <FileText className="w-5 h-5" />
                            Paper Summary
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <p className="text-slate-700 leading-relaxed">{studentDetails.evaluation_details.paper_summary}</p>
                        </CardContent>
                      </Card>
                    )}

                    {/* OI Score Hero Card */}
                    <Card className="border-2 border-purple-200 overflow-hidden">
                      <div className="h-2 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500"></div>
                      <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50">
                        <div className="flex items-center justify-between flex-wrap gap-4">
                          <div>
                            <CardTitle className="flex items-center gap-2 text-2xl">
                              <Trophy className="w-7 h-7 text-purple-600" />
                              Originality Index: 
                              <span className="text-purple-600">{selectedStudent.oi_score?.toFixed(1) || '-'}/10</span>
                            </CardTitle>
                            <p className="text-sm text-slate-600 mt-1">OI = 0.3×Novelty + 0.3×Resonance + 0.2×Synthesis + 0.2×Ethical</p>
                          </div>
                          <Badge className={`text-lg px-4 py-2 ${
                            (selectedStudent.oi_score || 0) >= 9 ? 'bg-emerald-600' :
                            (selectedStudent.oi_score || 0) >= 8 ? 'bg-green-600' :
                            (selectedStudent.oi_score || 0) >= 6 ? 'bg-purple-600' :
                            'bg-orange-600'
                          } text-white`}>
                            {studentDetails?.evaluation_details?.oi_interpretation || 
                             ((selectedStudent.oi_score || 0) >= 9 ? 'Exceptional' :
                              (selectedStudent.oi_score || 0) >= 8 ? 'Very High' :
                              (selectedStudent.oi_score || 0) >= 6 ? 'Moderate' : 'Developing')}
                          </Badge>
                        </div>
                      </CardHeader>
                      
                      <CardContent className="pt-6 space-y-6">
                        {/* Novelty - Purple */}
                        <div className="p-5 bg-purple-50 rounded-lg border-2 border-purple-200">
                          <div className="flex justify-between items-center mb-3">
                            <div>
                              <span className="font-bold text-lg text-purple-900">Novelty (Weight: 30%)</span>
                              <p className="text-xs text-purple-700 mt-1">How unprecedented and unique is this?</p>
                            </div>
                            <span className="bg-purple-600 text-white text-lg px-4 py-2 rounded-md font-semibold">
                              {selectedStudent.novelty_score?.toFixed(1) || '-'}/10
                            </span>
                          </div>
                          {studentDetails?.evaluation_details?.novelty_reasoning && (
                            <p className="text-sm text-slate-700 mb-3">{studentDetails.evaluation_details.novelty_reasoning}</p>
                          )}
                          {(studentDetails?.evaluation_details?.novelty_strengths?.length > 0 || 
                            studentDetails?.evaluation_details?.novelty_weaknesses?.length > 0) && (
                            <div className="mt-3 grid grid-cols-2 gap-3">
                              {studentDetails.evaluation_details.novelty_strengths?.length > 0 && (
                                <div className="p-2 bg-green-50 rounded border border-green-200">
                                  <p className="text-xs font-semibold text-green-900 mb-1">✓ Strengths:</p>
                                  <ul className="text-xs text-green-800 space-y-1">
                                    {studentDetails.evaluation_details.novelty_strengths.map((s, i) => (
                                      <li key={i}>• {s}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {studentDetails.evaluation_details.novelty_weaknesses?.length > 0 && (
                                <div className="p-2 bg-red-50 rounded border border-red-200">
                                  <p className="text-xs font-semibold text-red-900 mb-1">⚠ Weaknesses:</p>
                                  <ul className="text-xs text-red-800 space-y-1">
                                    {studentDetails.evaluation_details.novelty_weaknesses.map((w, i) => (
                                      <li key={i}>• {w}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Resonance - Blue */}
                        <div className="p-5 bg-blue-50 rounded-lg border-2 border-blue-200">
                          <div className="flex justify-between items-center mb-3">
                            <div>
                              <span className="font-bold text-lg text-blue-900">Resonance (Weight: 30%)</span>
                              <p className="text-xs text-blue-700 mt-1">How well does it address real needs?</p>
                            </div>
                            <span className="bg-blue-600 text-white text-lg px-4 py-2 rounded-md font-semibold">
                              {selectedStudent.resonance_score?.toFixed(1) || '-'}/10
                            </span>
                          </div>
                          {studentDetails?.evaluation_details?.resonance_reasoning && (
                            <p className="text-sm text-slate-700 mb-3">{studentDetails.evaluation_details.resonance_reasoning}</p>
                          )}
                          {(studentDetails?.evaluation_details?.resonance_strengths?.length > 0 || 
                            studentDetails?.evaluation_details?.resonance_weaknesses?.length > 0) && (
                            <div className="mt-3 grid grid-cols-2 gap-3">
                              {studentDetails.evaluation_details.resonance_strengths?.length > 0 && (
                                <div className="p-2 bg-green-50 rounded border border-green-200">
                                  <p className="text-xs font-semibold text-green-900 mb-1">✓ Strengths:</p>
                                  <ul className="text-xs text-green-800 space-y-1">
                                    {studentDetails.evaluation_details.resonance_strengths.map((s, i) => (
                                      <li key={i}>• {s}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {studentDetails.evaluation_details.resonance_weaknesses?.length > 0 && (
                                <div className="p-2 bg-red-50 rounded border border-red-200">
                                  <p className="text-xs font-semibold text-red-900 mb-1">⚠ Weaknesses:</p>
                                  <ul className="text-xs text-red-800 space-y-1">
                                    {studentDetails.evaluation_details.resonance_weaknesses.map((w, i) => (
                                      <li key={i}>• {w}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Synthesis - Green */}
                        <div className="p-5 bg-green-50 rounded-lg border-2 border-green-200">
                          <div className="flex justify-between items-center mb-3">
                            <div>
                              <span className="font-bold text-lg text-green-900">Synthesis (Weight: 20%)</span>
                              <p className="text-xs text-green-700 mt-1">How effectively does it combine concepts?</p>
                            </div>
                            <span className="bg-green-600 text-white text-lg px-4 py-2 rounded-md font-semibold">
                              {selectedStudent.synthesis_score?.toFixed(1) || '-'}/10
                            </span>
                          </div>
                          {studentDetails?.evaluation_details?.synthesis_reasoning && (
                            <p className="text-sm text-slate-700 mb-3">{studentDetails.evaluation_details.synthesis_reasoning}</p>
                          )}
                          {(studentDetails?.evaluation_details?.synthesis_strengths?.length > 0 || 
                            studentDetails?.evaluation_details?.synthesis_weaknesses?.length > 0) && (
                            <div className="mt-3 grid grid-cols-2 gap-3">
                              {studentDetails.evaluation_details.synthesis_strengths?.length > 0 && (
                                <div className="p-2 bg-green-50 rounded border border-green-200">
                                  <p className="text-xs font-semibold text-green-900 mb-1">✓ Strengths:</p>
                                  <ul className="text-xs text-green-800 space-y-1">
                                    {studentDetails.evaluation_details.synthesis_strengths.map((s, i) => (
                                      <li key={i}>• {s}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {studentDetails.evaluation_details.synthesis_weaknesses?.length > 0 && (
                                <div className="p-2 bg-red-50 rounded border border-red-200">
                                  <p className="text-xs font-semibold text-red-900 mb-1">⚠ Weaknesses:</p>
                                  <ul className="text-xs text-red-800 space-y-1">
                                    {studentDetails.evaluation_details.synthesis_weaknesses.map((w, i) => (
                                      <li key={i}>• {w}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Ethical - Orange */}
                        <div className="p-5 bg-orange-50 rounded-lg border-2 border-orange-200">
                          <div className="flex justify-between items-center mb-3">
                            <div>
                              <span className="font-bold text-lg text-orange-900">Ethical Anchoring (Weight: 20%)</span>
                              <p className="text-xs text-orange-700 mt-1">How responsible and inclusive is it?</p>
                            </div>
                            <span className="bg-orange-600 text-white text-lg px-4 py-2 rounded-md font-semibold">
                              {selectedStudent.ethical_score?.toFixed(1) || '-'}/10
                            </span>
                          </div>
                          {studentDetails?.evaluation_details?.ethical_reasoning && (
                            <p className="text-sm text-slate-700 mb-3">{studentDetails.evaluation_details.ethical_reasoning}</p>
                          )}
                          {(studentDetails?.evaluation_details?.ethical_strengths?.length > 0 || 
                            studentDetails?.evaluation_details?.ethical_weaknesses?.length > 0) && (
                            <div className="mt-3 grid grid-cols-2 gap-3">
                              {studentDetails.evaluation_details.ethical_strengths?.length > 0 && (
                                <div className="p-2 bg-green-50 rounded border border-green-200">
                                  <p className="text-xs font-semibold text-green-900 mb-1">✓ Strengths:</p>
                                  <ul className="text-xs text-green-800 space-y-1">
                                    {studentDetails.evaluation_details.ethical_strengths.map((s, i) => (
                                      <li key={i}>• {s}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {studentDetails.evaluation_details.ethical_weaknesses?.length > 0 && (
                                <div className="p-2 bg-red-50 rounded border border-red-200">
                                  <p className="text-xs font-semibold text-red-900 mb-1">⚠ Weaknesses:</p>
                                  <ul className="text-xs text-red-800 space-y-1">
                                    {studentDetails.evaluation_details.ethical_weaknesses.map((w, i) => (
                                      <li key={i}>• {w}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>

                    {/* MSA Score Card */}
                    <Card className="border-2 border-emerald-200">
                      <CardHeader className="bg-gradient-to-r from-emerald-50 to-cyan-50">
                        <CardTitle className="flex items-center gap-2 text-2xl">
                          <Zap className="w-7 h-7 text-emerald-600" />
                          Mirror-Spark Assessment
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-6">
                        <div className="mb-4">
                          <div className="flex justify-between mb-2">
                            <span className="text-sm font-semibold text-blue-600">
                              Mirror: {selectedStudent.msa_mirror_pct?.toFixed(0) || (100 - (selectedStudent.msa_spark_pct || 50)).toFixed(0)}%
                            </span>
                            <Badge className="bg-emerald-600 text-white text-lg px-4 py-2">
                              {studentDetails?.evaluation_details?.msa_assessment || 
                               ((selectedStudent.msa_spark_pct || 50) >= 60 ? 'High Spark' : 
                                (selectedStudent.msa_spark_pct || 50) >= 40 ? 'Balanced' : 'High Mirror')}
                            </Badge>
                            <span className="text-sm font-semibold text-emerald-600">
                              Spark: {selectedStudent.msa_spark_pct?.toFixed(0) || '50'}%
                            </span>
                          </div>
                          <div className="h-8 bg-slate-200 rounded-full overflow-hidden flex">
                            <div 
                              className="bg-blue-500 flex items-center justify-center text-white text-xs font-semibold"
                              style={{ width: `${selectedStudent.msa_mirror_pct || (100 - (selectedStudent.msa_spark_pct || 50))}%` }}
                            >
                              {(selectedStudent.msa_mirror_pct || (100 - (selectedStudent.msa_spark_pct || 50))) > 15 && 'Conventional'}
                            </div>
                            <div 
                              className="bg-emerald-500 flex items-center justify-center text-white text-xs font-semibold"
                              style={{ width: `${selectedStudent.msa_spark_pct || 50}%` }}
                            >
                              {(selectedStudent.msa_spark_pct || 50) > 15 && 'Original'}
                            </div>
                          </div>
                        </div>

                        <div className="p-4 bg-cyan-50 rounded-lg border border-cyan-200">
                          <h4 className="font-semibold text-cyan-900 mb-2">What This Means</h4>
                          <p className="text-sm text-cyan-800">
                            {studentDetails?.evaluation_details?.msa_interpretation || 
                             ((selectedStudent.msa_spark_pct || 50) >= 60 
                               ? "This work demonstrates strong human originality that goes beyond AI-replicable patterns. The ideas show creative insight and novel thinking."
                               : (selectedStudent.msa_spark_pct || 50) >= 40
                               ? "This work shows a balanced mix of conventional patterns and original thinking. It builds on existing ideas while adding unique perspectives."
                               : "This work primarily follows established patterns and conventions. Consider adding more unique insights or novel approaches to increase originality.")}
                          </p>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Generate Report Card - if no detailed data */}
                    {!studentDetails?.evaluation_details?.novelty_reasoning && 
                     !studentDetails?.evaluation_details?.paper_summary && (
                      <Card className="border-2 border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50">
                        <CardContent className="py-8 text-center">
                          <AlertCircle className="w-12 h-12 text-amber-600 mx-auto mb-4" />
                          <p className="text-lg text-amber-800 font-semibold mb-2">Detailed Analysis Not Available</p>
                          <p className="text-sm text-amber-700 mb-4 max-w-lg mx-auto">
                            This evaluation was completed before detailed report storage was enabled.
                            The original paper content is required to generate a comprehensive report with 
                            reasoning, strengths, and areas for improvement.
                          </p>
                          <div className="bg-white/60 rounded-lg p-4 max-w-md mx-auto border border-amber-200">
                            <p className="text-sm text-amber-900 font-medium mb-2">To get detailed reports:</p>
                            <ol className="text-xs text-amber-800 text-left space-y-1 list-decimal list-inside">
                              <li>Re-upload the student's paper</li>
                              <li>Run a new batch evaluation</li>
                              <li>New evaluations automatically include full detailed analysis</li>
                            </ol>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Footer */}
                    <div className="flex items-center justify-between pt-4 border-t border-slate-200 text-sm text-slate-600">
                      <div className="flex items-center gap-2">
                        <span>Status:</span>
                        <Badge className={statusColors[selectedStudent.evaluation_status || 'pending']}>
                          {selectedStudent.evaluation_status || 'pending'}
                        </Badge>
                      </div>
                      {selectedStudent.evaluated_at && (
                        <span>Evaluated: {new Date(selectedStudent.evaluated_at).toLocaleString()}</span>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
